/* ==========================================================================
   ModaWist — Product Detail Page behaviour
   - Variant selection (price, availability, Add to Bag state, URL, gallery)
   - Mobile gallery dots synced to swipe position
   - Sticky mobile Add to Bag bar
   - Wishlist toggle (localStorage)
   No external dependencies. Add-to-cart is handled by Dawn's <product-form>.
   ========================================================================== */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var STRINGS =
    window.variantStrings || {
      addToCart: 'Add to bag',
      soldOut: 'Sold out',
      unavailable: 'Unavailable',
    };

  function priceMarkup(variant) {
    if (variant.compare_at) {
      return '<ins>' + variant.price + '</ins><del>' + variant.compare_at + '</del>';
    }
    return variant.price;
  }

  function MwProduct(root) {
    this.root = root;
    var dataEl = root.querySelector('[data-mw-variants]');
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      this.variants = [];
    }

    this.idInput = root.querySelector('[data-mw-variant-id]');
    this.priceEl = root.querySelector('[data-mw-price]');
    this.stickyPriceEl = root.querySelector('[data-mw-price-sticky]');
    this.atc = root.querySelector('[data-mw-atc]');
    this.stickyAtc = root.querySelector('[data-mw-sticky-atc]');
    this.radios = Array.prototype.slice.call(root.querySelectorAll('input[name^="mw-option-"]'));

    this.bindOptions();
    this.initGallery();
    this.initStickyBar();
    this.initWishlist();
    this.initGift();

    // Sync UI with the initially-checked options.
    this.onChange();
  }

  /* ── Option selection ──────────────────────────────────────────────── */
  MwProduct.prototype.bindOptions = function () {
    var self = this;
    this.radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        self.onChange();
      });
    });
  };

  MwProduct.prototype.selectedOptions = function () {
    var selected = [];
    this.radios.forEach(function (radio) {
      if (radio.checked) {
        var pos = parseInt(radio.getAttribute('data-option-position'), 10) - 1;
        selected[pos] = radio.value;
      }
    });
    return selected;
  };

  MwProduct.prototype.findVariant = function (selected) {
    return this.variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return selected[i] === undefined || opt === selected[i];
      });
    });
  };

  MwProduct.prototype.onChange = function () {
    var selected = this.selectedOptions();
    var variant = this.findVariant(selected);

    this.refreshAvailability(selected);

    // Update the "Color: <value>" label.
    var self = this;
    this.radios.forEach(function (radio) {
      if (radio.checked) {
        var label = self.root.querySelector(
          '[data-mw-selected-label="' + radio.getAttribute('data-option-position') + '"]'
        );
        if (label) label.textContent = radio.value;
      }
    });

    if (!variant) {
      this.setUnavailable();
      return;
    }

    // Hidden id for the cart form.
    if (this.idInput) {
      this.idInput.value = variant.id;
      this.idInput.disabled = !variant.available;
    }

    this.setPrice(variant);
    this.setButtonState(variant);
    this.updateUrl(variant);
    this.showVariantMedia(variant);
  };

  MwProduct.prototype.refreshAvailability = function (selected) {
    var variants = this.variants;
    this.radios.forEach(function (radio) {
      var pos = parseInt(radio.getAttribute('data-option-position'), 10) - 1;
      var value = radio.value;
      var available = variants.some(function (v) {
        if (!v.available || v.options[pos] !== value) return false;
        return selected.every(function (sel, i) {
          return i === pos || sel === undefined || v.options[i] === sel;
        });
      });
      var label = radio.nextElementSibling;
      if (!label) return;
      label.classList.toggle('mw-pdp__size--soldout', label.classList.contains('mw-pdp__size') && !available);
      label.classList.toggle(
        'mw-pdp__swatch--soldout',
        label.classList.contains('mw-pdp__swatch') && !available
      );
    });
  };

  MwProduct.prototype.setPrice = function (variant) {
    if (this.priceEl) this.priceEl.innerHTML = priceMarkup(variant);
    if (this.stickyPriceEl) this.stickyPriceEl.textContent = variant.price;
  };

  MwProduct.prototype.setButtonState = function (variant) {
    var text = variant.available ? STRINGS.addToCart : STRINGS.soldOut;
    this.setButtonText(this.atc, text, !variant.available);
    this.setButtonText(this.stickyAtc, text, !variant.available);
  };

  MwProduct.prototype.setUnavailable = function () {
    if (this.idInput) this.idInput.disabled = true;
    this.setButtonText(this.atc, STRINGS.unavailable, true);
    this.setButtonText(this.stickyAtc, STRINGS.unavailable, true);
  };

  MwProduct.prototype.setButtonText = function (button, text, disabled) {
    if (!button) return;
    var span = button.querySelector('span');
    if (span) span.textContent = text;
    if (disabled) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  };

  MwProduct.prototype.updateUrl = function (variant) {
    if (!variant || this.root.dataset.mwUrlReady === undefined) {
      // Skip the very first sync so we don't push the initial state.
      this.root.dataset.mwUrlReady = 'true';
      return;
    }
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  };

  /* ── Gallery ───────────────────────────────────────────────────────── */
  MwProduct.prototype.initGallery = function () {
    this.track = this.root.querySelector('[data-mw-gallery]');
    this.dots = Array.prototype.slice.call(this.root.querySelectorAll('[data-mw-dots] .mw-pdp__dot'));
    this.thumbs = Array.prototype.slice.call(this.root.querySelectorAll('[data-mw-thumbs] .mw-pdp__thumb'));
    this.media = Array.prototype.slice.call(this.root.querySelectorAll('.mw-pdp__media'));
    if (!this.track) return;

    var self = this;

    // Thumbnail click → swap the main image (desktop).
    this.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        self.setActiveMedia(parseInt(thumb.getAttribute('data-index'), 10));
      });
    });

    // Dot click → go to that slide (mobile carousel).
    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.setActiveMedia(parseInt(dot.getAttribute('data-index'), 10));
      });
    });

    // Sync active dot with swipe position (mobile).
    if (this.dots.length) {
      var onScroll = function () {
        var width = self.track.clientWidth || 1;
        self.setActiveDot(Math.round(self.track.scrollLeft / width));
      };
      this.track.addEventListener('scroll', throttle(onScroll, 80), { passive: true });
    }
  };

  // Swap the visible main image (desktop) and sync thumbs/dots; on mobile,
  // scroll the carousel to the slide.
  MwProduct.prototype.setActiveMedia = function (index) {
    if (index < 0) return;
    this.media.forEach(function (m, i) {
      m.classList.toggle('mw-pdp__media--active', i === index);
    });
    this.thumbs.forEach(function (t, i) {
      t.classList.toggle('mw-pdp__thumb--active', i === index);
    });
    this.setActiveDot(index);

    if (window.matchMedia('(max-width: 1024px)').matches && this.track && this.track.children[index]) {
      this.track.children[index].scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  };

  MwProduct.prototype.setActiveDot = function (index) {
    this.dots.forEach(function (dot, i) {
      dot.classList.toggle('mw-pdp__dot--active', i === index);
    });
  };

  MwProduct.prototype.showVariantMedia = function (variant) {
    if (!variant.media_id || !this.media || !this.media.length) return;
    var target = String(variant.media_id);
    for (var i = 0; i < this.media.length; i++) {
      if (this.media[i].getAttribute('data-media-id') === target) {
        this.setActiveMedia(i);
        return;
      }
    }
  };

  /* ── Sticky mobile Add to Bag ──────────────────────────────────────── */
  MwProduct.prototype.initStickyBar = function () {
    this.stickyBar = this.root.querySelector('[data-mw-sticky]');
    if (!this.stickyBar || !this.atc || !('IntersectionObserver' in window)) return;

    var bar = this.stickyBar;
    var observer = new IntersectionObserver(
      function (entries) {
        bar.classList.toggle('mw-pdp__sticky-atc--visible', !entries[0].isIntersecting);
        bar.setAttribute('aria-hidden', entries[0].isIntersecting ? 'true' : 'false');
      },
      { threshold: 0 }
    );
    observer.observe(this.atc);
  };

  /* ── Wishlist ──────────────────────────────────────────────────────── */
  MwProduct.prototype.initWishlist = function () {
    var button = this.root.querySelector('[data-mw-wishlist]');
    if (!button) return;

    var handle = button.getAttribute('data-handle');
    var label = button.querySelector('[data-mw-wishlist-label]');

    var read = function () {
      try {
        return JSON.parse(localStorage.getItem('mw_wishlist') || '[]');
      } catch (e) {
        return [];
      }
    };

    var render = function (list) {
      var active = list.indexOf(handle) !== -1;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (label) label.textContent = active ? 'In Your Wishlist' : 'Add to Wishlist';
    };

    render(read());

    button.addEventListener('click', function () {
      var list = read();
      var i = list.indexOf(handle);
      if (i === -1) {
        list.push(handle);
      } else {
        list.splice(i, 1);
      }
      try {
        localStorage.setItem('mw_wishlist', JSON.stringify(list));
      } catch (e) {
        /* storage unavailable — ignore */
      }
      render(list);
    });
  };

  /* ── Gift wrapping add-on ──────────────────────────────────────────── */
  MwProduct.prototype.initGift = function () {
    this.gift = this.root.querySelector('[data-mw-gift]');
    if (!this.gift) return;
    var form = this.root.querySelector('product-form form');
    if (!form) return;
    var self = this;

    // Capture-phase so we run before Dawn's <product-form> submit handler.
    form.addEventListener(
      'submit',
      function (evt) {
        if (!self.gift.checked) return; // no gift → let Dawn add the main item
        evt.preventDefault();
        evt.stopImmediatePropagation();
        self.addWithGift();
      },
      true
    );
  };

  MwProduct.prototype.addWithGift = function () {
    var mainId = this.idInput && this.idInput.value;
    var giftId = this.gift.getAttribute('data-gift-id');
    if (!mainId || !giftId) return;

    var cart =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var sections = cart ? cart.getSectionsToRender().map(function (s) { return s.id; }) : [];
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var btn = this.atc;
    var spinner = btn && btn.querySelector('.loading__spinner');

    if (btn) {
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    }

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [
          { id: Number(mainId), quantity: 1 },
          { id: Number(giftId), quantity: 1 },
        ],
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (cart && res && !res.status) {
          cart.renderContents(res);
        } else {
          window.location = cartUrl;
        }
      })
      .catch(function () { window.location = cartUrl; })
      .finally(function () {
        if (btn) {
          btn.classList.remove('loading');
          btn.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        }
      });
  };

  /* ── utils ─────────────────────────────────────────────────────────── */
  function throttle(fn, wait) {
    var last = 0;
    var timer = null;
    return function () {
      var now = Date.now();
      var remaining = wait - (now - last);
      var ctx = this;
      var args = arguments;
      if (remaining <= 0) {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        last = now;
        fn.apply(ctx, args);
      } else if (!timer) {
        timer = setTimeout(function () {
          last = Date.now();
          timer = null;
          fn.apply(ctx, args);
        }, remaining);
      }
    };
  }

  function init() {
    document.querySelectorAll('[data-mw-pdp]').forEach(function (root) {
      if (root.dataset.mwInit) return;
      root.dataset.mwInit = 'true';
      new MwProduct(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
